import { Autocomplete, Box, Group, type RenderAutocompleteOption, Text } from "@mantine/core";
import { type CountryCode, getCountryCallingCode, isSupportedCountry } from "libphonenumber-js";
import { useState } from "react";
import { type CountryIso2, FlagImage } from "react-international-phone";

import classes from "./CountrySelect.module.css";
import { countryNameFilter, getCountryItems, getRegionName } from "./utils";

/**
 * libphonenumber and react-international-phone have different requirements for country code.
 * We store our data to libphonenumber's uppercase standard e.g "US"
 * so we must cast toLowerCase() to interface with react-international-phone
 *
 * @see https://react-international-phone.vercel.app/docs/Subcomponents-API/CountrySelector
 * @see https://www.npmjs.com/package/libphonenumber-js
 */
interface CountrySelectProps {
    clientEnabledCountries?: CountryCode[];
    setCountry: (country: CountryIso2) => void;
    disabled?: boolean;
    defaultCountry?: CountryCode;
}

const FlagIcon = ({ code }: { code: CountryCode }) => (
    <Box w="xl" className={classes.flagContainer}>
        <FlagImage iso2={code.toLowerCase()} />
    </Box>
);

export const CountryOption = ({ optionLabel, value }: { optionLabel: string; value: CountryCode }) => {
    return (
        <Group w="100%" gap="md">
            <FlagIcon code={value} />
            <Text span>{optionLabel}</Text>
        </Group>
    );
};

const renderOption: RenderAutocompleteOption = ({ option }): React.JSX.Element => {
    const countryCode = option.value as CountryCode;
    const regionName = getRegionName(countryCode);
    const dialCode = getCountryCallingCode(countryCode);
    const optionLabel = `${regionName} +${dialCode}`;

    return <CountryOption optionLabel={optionLabel} value={countryCode} />;
};

export function CountrySelect({
    clientEnabledCountries,
    setCountry,
    defaultCountry = "US",
    disabled = false,
}: CountrySelectProps) {
    const countryItems = getCountryItems(clientEnabledCountries);
    const [code, setCode] = useState<CountryCode>(defaultCountry);
    const [input, setInput] = useState<string>(defaultCountry);

    /**
     * Handles selection from the Autocomplete dropdown.
     *
     * - Verifies the selected item is a valid dropdown option.
     * - Sets input to uppercase code (e.g., "US") for libphonenumber-js compatibility.
     * - Updates the last valid code ref for snapback on blur.
     * - updates the parent with the lowercase code for react-international-phone compatibility.
     *
     * @param option - The `value` of the selected option
     * @returns void
     */
    const onOptionSubmit = (option: string) => {
        const selected = countryItems.find((c) => c.value === option);
        if (selected && isSupportedCountry(selected.value)) {
            setCode(selected.value);
            setCountry(selected.value.toLowerCase()); // react-international-phone expects lowercase country codes
        }
    };

    /**
     * Handles search input.
     *
     * Checks if user provided a valid country code. Normalizes input to uppercase
     * Since we use libphonenumber-js's isSupportedCountry().
     * If valid:
     *  - Short-circuits to render country code and flag.
     *  - Sets input to uppercase code (e.g., "US") for libphonenumber-js compatibility.
     *  - Sets Country to lowercase code (e.g., "us") for react-international-phone compatibility.
     *
     * @param input - Freeform user input
     * @returns void
     */
    const handleChange = (input: string) => {
        setInput(input);
        const formattedInput = input.trim().toUpperCase();
        if (isSupportedCountry(formattedInput)) {
            setInput(formattedInput);
            setCode(formattedInput);
        }
    };

    /**
     * Snap back to last valid country code if user
     * doesn't select a valid country. Prevent undefined
     * or invalid country codes from being set.
     */
    const handleBlur = () => {
        if (!isSupportedCountry(input)) {
            setInput(code);
        }
    };

    return (
        <Autocomplete
            value={input}
            classNames={{ input: classes.input }}
            data={countryItems}
            onChange={handleChange}
            onOptionSubmit={onOptionSubmit}
            disabled={disabled}
            leftSection={isSupportedCountry(input) && <FlagIcon code={code} />}
            renderOption={renderOption}
            comboboxProps={{
                position: "bottom-start",
            }}
            styles={{
                dropdown: {
                    minWidth: "fit-content",
                },
                input: {
                    backgroundColor: "var(--mantine-color-dark-8)",
                },
            }}
            filter={countryNameFilter}
            onBlur={handleBlur}
            data-testid="country-select"
            placeholder="Country"
        />
    );
}
